import { request, requestWithMsg, MsgResult } from './request';

// Schema 字段字典 (管理员管理 → Schema字段管理).
// 参数管理动态 Schema 的字段白名单, SSOT = xtool-schema 仓库:
// source='xtool-schema' 行只读 (seed 脚本同步), 只允许新增 source='custom' 字段.

export interface SchemaField {
    id: number;
    fieldKey: string;
    /** 字段显示名 (平台可编辑, xtool-schema 行也可改); 参数中心展示优先用它 */
    fieldName?: string;
    /** number / string / boolean / array / object / oneOf */
    fieldType: string;
    unit: string;
    /** 分组 (核心通用 / 矢量切割 / ...) */
    grp: string;
    /** 枚举可选值 JSON 数组字符串, 无约束为空 */
    enums?: string;
    /** 枚举来源字典类目 code (如 light_source/band); 非空=字典来源枚举, 与内联 enums 二选一 */
    enumsSource?: string;
    description?: string;
    /** xtool-schema = SSOT 镜像只读 / custom = 平台新增 */
    source: string;
    /** 来源 schema $id (xtool-schema 行) */
    schemaId?: string;
    remark?: string;
    createdAt?: number;
    updatedAt?: number;
}

export interface CreateSchemaFieldPayload {
    fieldKey: string;
    fieldName?: string;
    fieldType?: string;
    unit?: string;
    grp?: string;
    /** 枚举候选值 (标量数组); 编辑时传 [] 清空 */
    enums?: (string | number | boolean)[];
    /** 枚举来源字典类目 code; 编辑时传 '' 清空绑定 */
    enumsSource?: string;
    description?: string;
    remark?: string;
}

const BASE = '/v1';

export function listSchemaFields(): Promise<SchemaField[]> {
    return request<SchemaField[]>(`${BASE}/schema-fields`);
}

// ---- 模块级缓存 (同 capTypes 模式): 字典是小静态数据, 参数编辑器多实例共享一次请求 ----
let cache: SchemaField[] | null = null;
let inflight: Promise<SchemaField[]> | null = null;

export function fetchSchemaFieldsCached(): Promise<SchemaField[]> {
    if (cache) return Promise.resolve(cache);
    if (!inflight) {
        inflight = listSchemaFields()
            .then((r) => {
                cache = r || [];
                return cache;
            })
            .catch((e) => {
                inflight = null; // 失败不缓存, 下次重试
                throw e;
            });
    }
    return inflight;
}

/** Schema字段管理页增删后调用, 让参数编辑器下次重新拉取. */
export function invalidateSchemaFieldsCache() {
    cache = null;
    inflight = null;
}

/** 字典 → {fieldKey: 显示名} Map (无显示名的 key 不入 Map, 展示回退 key 本身). */
export function toFieldNameMap(dict: SchemaField[]): Map<string, string> {
    return new Map(dict.filter((d) => d.fieldName).map((d) => [d.fieldKey, d.fieldName!]));
}

/** 字典字段的建议显示名: fieldName > description 首段 (到第一个括号/句号) > key. */
export function suggestFieldLabel(d: SchemaField): string {
    if (d.fieldName) return d.fieldName;
    const m = (d.description || '').trim().match(/^([^（(。,，]{1,16})/);
    return m ? m[1] : d.fieldKey;
}

/** 解析字典字段的 enums JSON → 标量数组 (容错, 无约束返回 []). */
export function parseFieldEnums(d: SchemaField): (string | number | boolean)[] {
    try {
        const parsed = d.enums && d.enums !== 'null' ? JSON.parse(d.enums) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/** 参数中心展示字段用的名字: 只认字段字典 (2026-09-02 拍板), 字典没名才回退 key.
 *  模板/行快照里的 label 是历史遗留 (v4 迁移期字典还没有中文名), 不再参与显示 —
 *  改名统一在「Schema 字段管理」维护, 全站即时生效. */
export function fieldDisplayName(k: string, names: Map<string, string> | undefined): string {
    return names?.get(k) || k;
}

/** 新增 custom 字段 (仅管理员); xtool-schema 行只能由 seed 脚本产生. */
export function createSchemaField(
    payload: CreateSchemaFieldPayload,
): Promise<MsgResult<{ id?: number; fieldKey?: string }>> {
    return requestWithMsg(`${BASE}/schema-fields`, { method: 'POST', body: payload });
}

/** 编辑 custom 字段 (仅管理员, key 不可改; xtool-schema 行后端拒绝). */
export function updateSchemaField(
    fieldKey: string,
    payload: Omit<CreateSchemaFieldPayload, 'fieldKey'>,
): Promise<MsgResult<null>> {
    // 业务键走 body (后端 updateReq.fieldKey 必填; payload 类型里已 Omit 掉它)
    return requestWithMsg(`${BASE}/schema-fields/update`, {
        method: 'POST',
        body: { fieldKey, ...payload },
    });
}

/** 删除 custom 字段 (仅管理员, xtool-schema 行后端拒绝). */
export function deleteSchemaField(fieldKey: string): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/schema-fields/delete`, {
        method: 'POST',
        body: { fieldKey },
    });
}
